// @ts-nocheck
import React from 'react';
import { Box, Skeleton, Stack, FormControlLabel, Switch, Alert } from '@mui/material';

interface ForecastLoadingStateProps {
  /** Number of category rows to show in skeleton */
  categoryCount?: number;
  /** Custom styles for the container */
  containerStyles?: string;
  /** Show balance warning skeleton */
  showBalanceWarning?: boolean;
}

const ForecastLoadingState: React.FC<ForecastLoadingStateProps> = ({
  categoryCount = 5,
  containerStyles = '',
  showBalanceWarning = false,
}) => {
  // Generate skeleton rows for Growth Adjustment table
  const generateGrowthTableRows = (rowCount: number) => {
    return Array.from({ length: rowCount }, (_, index) => (
      <Stack
        key={index}
        direction="row"
        spacing={2}
        sx={{
          py: 1,
          px: 2,
          borderBottom: '1px solid #f0f0f0',
          alignItems: 'center',
        }}
      >
        {/* Category name column */}
        <Box sx={{ flex: 1 }}>
          <Skeleton variant="text" width="80%" height={24} />
        </Box>

        {/* Input fields for years 2025-2029 */}
        {Array.from({ length: 5 }, (_, yearIndex) => (
          <Box key={yearIndex} sx={{ flex: 1 }}>
            <Skeleton variant="rectangular" width="100%" height={40} sx={{ borderRadius: 1 }} />
          </Box>
        ))}
      </Stack>
    ));
  };

  // Generate skeleton rows for Revenue table
  const generateRevenueTableRows = (rowCount: number) => {
    return Array.from({ length: rowCount + 1 }, (_, index) => (
      <Stack
        key={index}
        direction="row"
        spacing={2}
        sx={{
          py: 1.5,
          px: 2,
          borderBottom: '1px solid #f0f0f0',
          alignItems: 'center',
          backgroundColor: index === rowCount ? '#f8f9fa' : 'transparent', // Total row styling
        }}
      >
        {/* Category name column */}
        <Box sx={{ flex: 1 }}>
          <Skeleton
            variant="text"
            width={index === rowCount ? '40%' : '75%'}
            height={24}
            sx={{ fontWeight: index === rowCount ? 'bold' : 'normal' }}
          />
        </Box>

        {/* Revenue columns for years 2024-2029 + CAGR */}
        {Array.from({ length: 7 }, (_, yearIndex) => (
          <Box key={yearIndex} sx={{ flex: 1, textAlign: 'center' }}>
            <Skeleton variant="text" width="80%" height={24} />
          </Box>
        ))}
      </Stack>
    ));
  };

  // Generate table header
  const generateTableHeader = (headers: string[]) => (
    <Stack
      direction="row"
      spacing={2}
      sx={{
        py: 2,
        px: 2,
        backgroundColor: '#f5f5f5',
        borderBottom: '2px solid #e0e0e0',
        fontWeight: 'bold',
      }}
    >
      {headers.map((header, index) => (
        <Box key={index} sx={{ flex: 1 }}>
          <Skeleton variant="text" width="70%" height={20} />
        </Box>
      ))}
    </Stack>
  );

  return (
    <Box className={containerStyles}>
      {/* Main Forecast Card */}
      <Box
        className="modelCard"
        sx={{ border: '1px solid #e0e0e0', borderRadius: 2, overflow: 'hidden' }}
      >
        {/* Forecast Header */}
        <Box sx={{ p: 3, pb: 2 }}>
          <Skeleton variant="text" width="150px" height={32} sx={{ mb: 2 }} />
        </Box>

        {/* Balance Warning Skeleton (conditional) */}
        {showBalanceWarning && (
          <Box sx={{ px: 3, pb: 2 }}>
            <Alert severity="warning" sx={{ opacity: 0.7 }}>
              <Skeleton variant="text" width="100%" height={20} />
              <Skeleton variant="text" width="60%" height={20} />
            </Alert>
          </Box>
        )}

        {/* Growth Adjustment Section */}
        <Box sx={{ px: 3, pb: 3 }}>
          {/* Section Header */}
          <Box sx={{ mb: 2 }}>
            <Skeleton variant="text" width="180px" height={28} />
          </Box>

          {/* Growth Table Container */}
          <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, overflow: 'hidden' }}>
            {/* Table Header */}
            {generateTableHeader(['Category', '2025', '2026', '2027', '2028', '2029'])}

            {/* Table Rows */}
            {generateGrowthTableRows(categoryCount)}
          </Box>
        </Box>

        {/* Revenue Section */}
        <Box sx={{ px: 3, pb: 3 }}>
          {/* Instruction Row with Switch */}
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Skeleton variant="text" width="350px" height={24} />
            <FormControlLabel
              control={<Switch disabled sx={{ opacity: 0.5 }} />}
              label={<Skeleton variant="text" width="120px" height={20} />}
              labelPlacement="start"
            />
          </Stack>

          {/* Revenue Section Header */}
          <Box sx={{ mb: 2 }}>
            <Skeleton variant="text" width="120px" height={28} />
          </Box>

          {/* Revenue Table Container */}
          <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, overflow: 'hidden' }}>
            {/* Revenue Table Header */}
            {generateTableHeader([
              'Category',
              '2024',
              '2025',
              '2026',
              '2027',
              '2028',
              '2029',
              'CAGR',
            ])}

            {/* Revenue Table Rows */}
            {generateRevenueTableRows(categoryCount)}
          </Box>
        </Box>

        {/* Submit Button Skeleton */}
        <Box sx={{ px: 3, pb: 3 }}>
          <Skeleton variant="rectangular" width="80px" height={36} sx={{ borderRadius: 1 }} />
        </Box>
      </Box>
    </Box>
  );
};

export default ForecastLoadingState;
